import React, { useState, useMemo } from 'react';
import { Descriptions, Button, Space, message, Tooltip, Tag, Input, Table } from 'antd';
import { CopyOutlined, FileExcelOutlined, SearchOutlined } from '@ant-design/icons';
import { exportToCSV } from '../../utils/exportUtils';
import { useTranslation } from 'react-i18next';
import dayjs from '../../utils/dayjs';

const GUID_REGEX = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
const URL_REGEX = /^https?:\/\/.+/i;

const preBlockStyle = {
  margin: 0,
  fontSize: 12,
  fontFamily: 'monospace',
  maxHeight: 280,
  overflow: 'auto',
  background: '#fafafa',
  padding: 12,
  borderRadius: 4,
  border: '1px solid #f0f0f0',
  whiteSpace: 'pre-wrap',
  wordBreak: 'break-word',
  width: '100%',
  boxSizing: 'border-box',
};

const JsonView = ({ data, title, fileName = 'export', compact = false, search: controlledSearch, onSearchChange }) => {
  const { t } = useTranslation();
  const [copied, setCopied] = useState(false);
  const [internalSearch, setInternalSearch] = useState('');
  const search = controlledSearch !== undefined ? controlledSearch : internalSearch;
  const setSearch = onSearchChange || setInternalSearch;
  const filterInHeader = controlledSearch !== undefined;

  const extractFirstGuid = (obj) => {
    if (typeof obj !== 'object' || obj === null) return null;
    
    if (obj.id && typeof obj.id === 'string' && GUID_REGEX.test(obj.id)) {
      return obj.id;
    }
    
    for (const value of Object.values(obj)) {
      if (typeof value === 'string' && GUID_REGEX.test(value)) {
        return value;
      }
      if (Array.isArray(value)) {
        for (const item of value) {
          if (typeof item === 'string' && GUID_REGEX.test(item)) {
            return item;
          }
          if (typeof item === 'object' && item !== null) {
            const guid = extractFirstGuid(item);
            if (guid) return guid;
          }
        }
      }
      if (typeof value === 'object' && value !== null) {
        const guid = extractFirstGuid(value);
        if (guid) return guid;
      }
    }
    return null;
  };

  const exportFileName = useMemo(() => {
    if (!data) return fileName;
    const guid = extractFirstGuid(data);
    return guid || fileName;
  }, [data, fileName]);

  const isISODate = (str) => {
    if (typeof str !== 'string') return false;
    const isoDateRegex = /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}/;
    if (!isoDateRegex.test(str)) return false;
    const parsed = dayjs(str);
    return parsed.isValid();
  };

  const isBoolean = (value) => {
    return typeof value === 'boolean' || value === 'true' || value === 'false' || value === 'True' || value === 'False';
  };

  const isGuid = (value) => {
    return typeof value === 'string' && GUID_REGEX.test(value);
  };

  const isUrl = (value) => {
    return typeof value === 'string' && URL_REGEX.test(value);
  };

  const isEmpty = (value) => {
    if (value === null || value === undefined) return true;
    if (typeof value === 'string' && value.trim() === '') return true;
    if (Array.isArray(value) && value.length === 0) return true;
    if (typeof value === 'object' && Object.keys(value).length === 0) return true;
    return false;
  };

  const isArrayOfObjects = (arr) => {
    return Array.isArray(arr) && arr.length > 0 && arr.every((item) => item !== null && typeof item === 'object' && !Array.isArray(item));
  };

  const formatCellValue = (value) => {
    if (value === null || value === undefined) return '—';
    if (typeof value === 'object') {
      return <pre style={{ ...preBlockStyle, maxHeight: 120, fontSize: 11 }}>{JSON.stringify(value, null, 2)}</pre>;
    }
    if (isBoolean(value)) {
      const boolVal = value === true || value === 'true' || value === 'True';
      return <Tag color={boolVal ? 'green' : 'default'}>{boolVal ? 'Yes' : 'No'}</Tag>;
    }
    if (isGuid(value)) {
      return <span style={{ fontFamily: 'monospace', fontSize: 11 }}>{value}</span>;
    }
    if (isUrl(value)) {
      return <a href={value} target="_blank" rel="noopener noreferrer">{value}</a>;
    }
    const stringValue = String(value);
    if (isISODate(stringValue)) {
      const d = dayjs(stringValue);
      return d.isValid() ? d.format('YYYY-MM-DD HH:mm') : stringValue;
    }
    return stringValue;
  };

  const renderArrayAsTable = (arr) => {
    const keys = [...new Set(arr.flatMap((row) => Object.keys(row)))];
    const columns = keys.map((key) => {
      const title = key.charAt(0).toUpperCase() + key.slice(1).replace(/([A-Z])/g, ' $1').trim();
      return {
        key,
        dataIndex: key,
        title,
        render: (val) => formatCellValue(val),
        ellipsis: true,
      };
    });
    return (
      <div style={{ overflowX: 'auto', width: '100%' }}>
        <Table
          size="small"
          dataSource={arr.map((row, i) => ({ ...row, key: i }))}
          columns={columns}
          pagination={arr.length > 10 ? { pageSize: 10, size: 'small' } : false}
          scroll={{ x: 'max-content' }}
        />
      </div>
    );
  };

  const convertToItems = (obj, prefix = '') => {
    const items = [];
    let keyIndex = 1;

    const processValue = (value, label) => {
      if (value === null || value === undefined) {
        return '-';
      }
      if (Array.isArray(value)) {
        if (isArrayOfObjects(value)) {
          return renderArrayAsTable(value);
        }
        return <pre style={preBlockStyle}>{JSON.stringify(value, null, 2)}</pre>;
      }
      if (typeof value === 'object') {
        return <pre style={preBlockStyle}>{JSON.stringify(value, null, 2)}</pre>;
      }

      if (isBoolean(value)) {
        const boolVal = value === true || value === 'true' || value === 'True';
        return <Tag color={boolVal ? 'green' : 'default'}>{boolVal ? 'Yes' : 'No'}</Tag>;
      }
      if (isGuid(value)) {
        return <span style={{ fontFamily: 'monospace', fontSize: '12px' }}>{value}</span>;
      }
      if (isUrl(value)) {
        return <a href={value} target="_blank" rel="noopener noreferrer">{value}</a>;
      }
      const stringValue = String(value);
      if (isISODate(stringValue)) {
        const d = dayjs(stringValue);
        return d.isValid() ? d.format('YYYY-MM-DD HH:mm') : stringValue;
      }
      return stringValue;
    };

    Object.entries(obj).forEach(([key, value]) => {
      if (isEmpty(value)) {
        return;
      }

      const label = prefix ? `${prefix}.${key}` : key;
      const displayLabel = label.charAt(0).toUpperCase() + label.slice(1).replace(/([A-Z])/g, ' $1').trim();
      
      const isArray = Array.isArray(value);
      const labelStyle = isArray ? { fontWeight: 'bold' } : {};
      
      items.push({
        key: String(keyIndex++),
        label: <span style={labelStyle}>{displayLabel}</span>,
        children: processValue(value, label),
        searchLabel: displayLabel,
      });
    });

    return items;
  };

  const allItems = useMemo(() => {
    if (!data) return [];
    return convertToItems(data);
  }, [data]);
  const items = useMemo(() => {
    if (!search.trim()) return allItems;
    const q = search.trim().toLowerCase();
    return allItems.filter((item) => (item.searchLabel || '').toLowerCase().includes(q));
  }, [allItems, search]);

  if (!data) {
    return null;
  }

  if (compact) {
    const jsonStr = JSON.stringify(data, null, 2);
    const handleCopyCompact = async () => {
      try {
        await navigator.clipboard.writeText(jsonStr);
        setCopied(true);
        message.success(t('Copied to clipboard'));
        setTimeout(() => setCopied(false), 2000);
      } catch {
        message.error(t('Failed to copy'));
      }
    };
    const handleExportCompact = () => {
      try {
        exportToCSV(data, exportFileName);
        message.success(t('CSV exported successfully'));
      } catch {
        message.error(t('Failed to export CSV'));
      }
    };
    return (
      <div style={{ display: 'flex', flexDirection: 'column', height: '100%', minHeight: 0 }}>
        <Space style={{ marginBottom: 12, flexShrink: 0 }}>
          <Tooltip title={copied ? t('Copied!') : t('Copy JSON')}>
            <Button icon={<CopyOutlined />} onClick={handleCopyCompact} size="small" />
          </Tooltip>
          <Tooltip title={t('Export CSV')}>
            <Button icon={<FileExcelOutlined />} onClick={handleExportCompact} size="small" />
          </Tooltip>
        </Space>
        <pre style={{ ...preBlockStyle, flex: 1, maxHeight: 'none', minHeight: 200 }}>{jsonStr}</pre>
      </div>
    );
  }

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(JSON.stringify(data, null, 2));
      setCopied(true);
      message.success(t('Copied to clipboard'));
      setTimeout(() => setCopied(false), 2000);
    } catch (error) {
      message.error(t('Failed to copy'));
    }
  };

  const handleExportCSV = () => {
    try {
      exportToCSV(data, exportFileName);
      message.success(t('CSV exported successfully'));
    } catch (error) {
      message.error(t('Failed to export CSV'));
    }
  };

  const descItems = items.map(({ key, label, children }) => ({ key, label, children }));

  return (
    <div style={{ width: '100%' }}>
      {!filterInHeader && (
        <Space direction="vertical" style={{ width: '100%', marginBottom: 12 }} size={8}>
          <Input
            placeholder={t('Filter properties...')}
            prefix={<SearchOutlined style={{ color: '#bfbfbf' }} />}
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            allowClear
            size="small"
            style={{ width: '100%' }}
          />
          <Space>
            {title && <h3 style={{ margin: 0 }}>{title}</h3>}
            <Tooltip title={copied ? t('Copied!') : t('Copy JSON')}>
              <Button icon={<CopyOutlined />} onClick={handleCopy} size="small" />
            </Tooltip>
            <Tooltip title={t('Export CSV')}>
              <Button icon={<FileExcelOutlined />} onClick={handleExportCSV} size="small" />
            </Tooltip>
          </Space>
        </Space>
      )}
      {filterInHeader && (
        <Space style={{ marginBottom: 12 }}>
          {title && <h3 style={{ margin: 0 }}>{title}</h3>}
          <Tooltip title={copied ? t('Copied!') : t('Copy JSON')}>
            <Button icon={<CopyOutlined />} onClick={handleCopy} size="small" />
          </Tooltip>
          <Tooltip title={t('Export CSV')}>
            <Button icon={<FileExcelOutlined />} onClick={handleExportCSV} size="small" />
          </Tooltip>
        </Space>
      )}
      <Descriptions column={1} layout="vertical" items={descItems} />
    </div>
  );
};

export default JsonView;
